/**
 * GPX File Parser Utility
 * Parses GPX (GPS Exchange Format) files to extract route coordinates
 */

export interface GpxParseResult {
  /** Array of [lat, lng] coordinates */
  coordinates: [number, number][]
  /** Route/track name from GPX file */
  name?: string
  /** Total distance in meters */
  distanceMeters: number
  /** Number of points in the route */
  pointCount: number
}

export interface GpxParseError {
  message: string
  details?: string
}

/**
 * Calculates the distance between two points using the Haversine formula
 * @param p1 First point [lat, lng]
 * @param p2 Second point [lat, lng]
 * @returns Distance in meters
 */
function haversineDistance(p1: [number, number], p2: [number, number]): number {
  const R = 6371000 // Earth's radius in meters
  const lat1 = p1[0] * Math.PI / 180
  const lat2 = p2[0] * Math.PI / 180
  const dLat = (p2[0] - p1[0]) * Math.PI / 180
  const dLon = (p2[1] - p1[1]) * Math.PI / 180

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * Calculates total distance of a path
 * @param points Array of [lat, lng] coordinates
 * @returns Total distance in meters
 */
function calculatePathDistance(points: [number, number][]): number {
  let total = 0
  for (let i = 1; i < points.length; i++) {
    total += haversineDistance(points[i - 1], points[i])
  }
  return total
}

/**
 * Parses a GPX file content and extracts route coordinates
 * @param gpxContent The raw XML content of the GPX file
 * @returns Parsed result with coordinates and metadata, or error
 */
export function parseGpxFile(gpxContent: string): GpxParseResult | GpxParseError {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(gpxContent, 'application/xml')

    // Check for XML parsing errors
    const parseError = doc.querySelector('parsererror')
    if (parseError) {
      return {
        message: 'Invalid GPX file format',
        details: parseError.textContent || 'XML parsing failed',
      }
    }

    const coordinates: [number, number][] = []

    // Try to extract track points first (most common in GPX files)
    const trackPoints = doc.querySelectorAll('trkpt')
    trackPoints.forEach(pt => {
      const lat = parseFloat(pt.getAttribute('lat') || '')
      const lon = parseFloat(pt.getAttribute('lon') || '')
      if (!isNaN(lat) && !isNaN(lon)) {
        coordinates.push([lat, lon])
      }
    })

    // If no track points, try route points
    if (coordinates.length === 0) {
      const routePoints = doc.querySelectorAll('rtept')
      routePoints.forEach(pt => {
        const lat = parseFloat(pt.getAttribute('lat') || '')
        const lon = parseFloat(pt.getAttribute('lon') || '')
        if (!isNaN(lat) && !isNaN(lon)) {
          coordinates.push([lat, lon])
        }
      })
    }

    // If still no points, try waypoints
    if (coordinates.length === 0) {
      const waypoints = doc.querySelectorAll('wpt')
      waypoints.forEach(pt => {
        const lat = parseFloat(pt.getAttribute('lat') || '')
        const lon = parseFloat(pt.getAttribute('lon') || '')
        if (!isNaN(lat) && !isNaN(lon)) {
          coordinates.push([lat, lon])
        }
      })
    }

    if (coordinates.length === 0) {
      return {
        message: 'No coordinates found in GPX file',
        details: 'The file does not contain any track points (trkpt), route points (rtept), or waypoints (wpt).',
      }
    }

    // Try to get the route/track name
    let name: string | undefined
    const nameEl = doc.querySelector('trk > name') ||
                   doc.querySelector('rte > name') ||
                   doc.querySelector('metadata > name') ||
                   doc.querySelector('name')
    if (nameEl?.textContent) {
      name = nameEl.textContent.trim()
    }

    // Calculate total distance
    const distanceMeters = calculatePathDistance(coordinates)

    return {
      coordinates,
      name,
      distanceMeters,
      pointCount: coordinates.length,
    }
  } catch (error) {
    return {
      message: 'Failed to parse GPX file',
      details: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Simplifies a route by reducing the number of points while preserving the shape
 * Uses the Ramer-Douglas-Peucker algorithm
 * @param points Array of [lat, lng] coordinates
 * @param tolerance Tolerance in meters (points within this distance of the simplified line are removed)
 * @returns Simplified array of coordinates
 */
export function simplifyRoute(points: [number, number][], tolerance: number = 10): [number, number][] {
  if (points.length <= 2) return points

  // Find the point with the maximum distance from the line between first and last
  let maxDistance = 0
  let maxIndex = 0

  const first = points[0]
  const last = points[points.length - 1]

  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(points[i], first, last)
    if (distance > maxDistance) {
      maxDistance = distance
      maxIndex = i
    }
  }

  // If max distance is greater than tolerance, recursively simplify
  if (maxDistance > tolerance) {
    const left = simplifyRoute(points.slice(0, maxIndex + 1), tolerance)
    const right = simplifyRoute(points.slice(maxIndex), tolerance)
    return [...left.slice(0, -1), ...right]
  }

  // Otherwise, return just the endpoints
  return [first, last]
}

/**
 * Calculates the perpendicular distance from a point to a line
 */
function perpendicularDistance(
  point: [number, number],
  lineStart: [number, number],
  lineEnd: [number, number]
): number {
  // Convert to approximate meters for small distances
  const dx = (lineEnd[1] - lineStart[1]) * Math.cos(lineStart[0] * Math.PI / 180) * 111320
  const dy = (lineEnd[0] - lineStart[0]) * 110540

  if (dx === 0 && dy === 0) {
    return haversineDistance(point, lineStart)
  }

  const px = (point[1] - lineStart[1]) * Math.cos(lineStart[0] * Math.PI / 180) * 111320
  const py = (point[0] - lineStart[0]) * 110540

  const t = Math.max(0, Math.min(1, (px * dx + py * dy) / (dx * dx + dy * dy)))

  const closestX = lineStart[1] + t * (lineEnd[1] - lineStart[1])
  const closestY = lineStart[0] + t * (lineEnd[0] - lineStart[0])

  return haversineDistance(point, [closestY, closestX])
}

/**
 * Type guard to check if result is an error
 */
export function isGpxParseError(result: GpxParseResult | GpxParseError): result is GpxParseError {
  return 'message' in result && !('coordinates' in result)
}
